import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import PageHeader from './PageHeader';

/**
 * Blocos de carregamento com a forma do conteúdo que vai aparecer.
 *
 * Cores seguem os tokens do projeto: barra `bg-zinc-700` sobre card `bg-zinc-800`
 * no dark, `bg-gray-200` sobre `bg-white` no light. Os skeletons antigos de
 * Analytics usam `bg-gray-300` fixo, que fica claro demais no dark.
 *
 * A convenção do projeto é `darkMode` do useTheme() em template string, não a
 * diretiva `dark:` do Tailwind (só 2 arquivos usam `dark:`).
 */

export function Skeleton({ className = '', rounded = 'rounded' }) {
  const { darkMode } = useTheme();
  return (
    <div
      aria-hidden="true"
      className={`animate-pulse ${rounded} ${
        darkMode ? 'bg-zinc-700' : 'bg-gray-200'
      } ${className}`}
    />
  );
}

/** Linhas de texto de larguras variadas, para não parecer um bloco só. */
export function SkeletonTexto({ linhas = 3, className = '' }) {
  const larguras = ['w-full', 'w-11/12', 'w-4/5', 'w-3/4', 'w-2/3'];
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: linhas }).map((_, i) => (
        <Skeleton key={i} className={`h-3 ${larguras[i % larguras.length]}`} />
      ))}
    </div>
  );
}

/** Card com a forma do MetricCard: label, valor e ícone. */
export function SkeletonMetricCard() {
  const { darkMode } = useTheme();
  return (
    <div
      className={`rounded-lg shadow-sm border p-4 sm:p-6 ${
        darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-7 w-3/4" />
        </div>
        <Skeleton className="h-9 w-9 sm:h-12 sm:w-12 shrink-0" rounded="rounded-lg" />
      </div>
    </div>
  );
}

/** Card genérico de conteúdo, com título opcional. */
export function SkeletonCard({ linhas = 4, titulo = true, className = '' }) {
  const { darkMode } = useTheme();
  return (
    <div
      className={`rounded-lg shadow-sm border p-4 ${
        darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'
      } ${className}`}
    >
      {titulo && <Skeleton className="h-5 w-1/3 mb-4" />}
      <SkeletonTexto linhas={linhas} />
    </div>
  );
}

/** Linha com avatar circular + duas linhas de texto. Serve lista e inbox. */
export function SkeletonLinhaContato() {
  return (
    <div className="flex items-center gap-3 p-3">
      <Skeleton className="h-10 w-10 shrink-0" rounded="rounded-full" />
      <div className="flex-1 space-y-2 min-w-0">
        <Skeleton className="h-3.5 w-2/5" />
        <Skeleton className="h-3 w-3/4" />
      </div>
      <Skeleton className="h-3 w-10 shrink-0" />
    </div>
  );
}

export function SkeletonTabela({ colunas = 5, linhas = 8 }) {
  const { darkMode } = useTheme();
  return (
    <div
      className={`rounded-lg border overflow-hidden ${
        darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      <div
        className={`flex gap-4 px-4 py-3 ${
          darkMode ? 'bg-zinc-900' : 'bg-gray-50'
        }`}
      >
        {Array.from({ length: colunas }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
      <div className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
        {Array.from({ length: linhas }).map((_, l) => (
          <div key={l} className="flex gap-4 px-4 py-4">
            {Array.from({ length: colunas }).map((_, c) => (
              <Skeleton key={c} className="h-3.5 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SkeletonKanban({ colunas = 4, cardsPorColuna = 3 }) {
  const { darkMode } = useTheme();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      {Array.from({ length: colunas }).map((_, i) => (
        <div
          key={i}
          className={`rounded-xl border p-3 min-h-[400px] ${
            darkMode ? 'bg-zinc-800/50 border-zinc-700' : 'bg-gray-50 border-gray-200'
          }`}
        >
          <Skeleton className="h-4 w-2/5 mb-4" />
          <div className="space-y-2">
            {Array.from({ length: cardsPorColuna }).map((_, c) => (
              <div
                key={c}
                className={`rounded-lg p-3 space-y-2 ${
                  darkMode ? 'bg-zinc-800' : 'bg-white'
                }`}
              >
                <Skeleton className="h-3.5 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Grade de cards, para as telas de card grid (segmentos, grupos, templates). */
export function SkeletonGrid({ itens = 6, colunas = 3 }) {
  const cls =
    { 2: 'md:grid-cols-2', 3: 'md:grid-cols-2 lg:grid-cols-3', 4: 'md:grid-cols-2 lg:grid-cols-4' }[
      colunas
    ] || 'md:grid-cols-2 lg:grid-cols-3';
  return (
    <div className={`grid grid-cols-1 ${cls} gap-4`}>
      {Array.from({ length: itens }).map((_, i) => (
        <SkeletonCard key={i} linhas={3} />
      ))}
    </div>
  );
}

/**
 * Casca de página. `title`/`subtitle` são estáticos, então renderiza o
 * PageHeader de verdade — só o conteúdo abaixo é skeleton. Nas telas de
 * detalhe o título vem da entidade carregada, e aí vira barra.
 */
export function PageSkeleton({ title, subtitle, children }) {
  return (
    <div className="space-y-6">
      {title ? (
        <PageHeader title={title} subtitle={subtitle} />
      ) : (
        <div className="space-y-2">
          <Skeleton className="h-7 w-56" />
          <Skeleton className="h-4 w-72 max-w-full" />
        </div>
      )}
      {children}
    </div>
  );
}

export default Skeleton;
